// apps/admin/src/pages/vendors/PendingVendorsPage.tsx
//
// Phase 1 Task 50 — Admin Pending Vendors page.
//
// Per CONTEXT — vendor approval flow.
// Goals: (vendor approval admin surface).
//
// Behavior:
//   - <Table> from @printway/ui (Task 41) listing GET /api/v1/admin/vendors/pending
//     (offset pagination — admin).
//   - Sortable: appliedAt | displayName.
//   - Click a row → opens <VendorReviewDrawer> with vendor detail panel.
//
// Wire shape comes from PostAdminVendorsPendingResponse / GetAdminVendorsPendingQuery
// in API-CONTRACTS.md L2362+. All envelope unwrap follows.

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Table,
  type TableColumn,
  type TableState,
  useToast,
  PageHeader,
} from '@printway/ui';
import { apiClient } from '@printway/shared/api';
import { VendorReviewDrawer } from './VendorReviewDrawer.js';
import { useTranslation } from '../../i18n/useTranslation.js';

// ---------------------------------------------------------------------------
// Wire types — slim FE mirror of API-CONTRACTS L2388-2407.
// (Once `pnpm api:sync` runs we can substitute paths['/admin/vendors/pending']
// .get.responses['200']['content']['application/json']['schema']['data'].)
// ---------------------------------------------------------------------------

interface PendingVendorRow extends Record<string, unknown> {
  id: string;
  email: string;
  displayName: string;
  appliedAt: string;
  surveyCompletedAt: string | null;
  countryCode: string;
}

interface PendingVendorsResponse {
  vendors: PendingVendorRow[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

interface SuccessEnvelope<T> {
  success: true;
  data: T;
  requestId: string;
}

function unwrap<T>(payload: unknown): T {
  if (
    payload &&
    typeof payload === 'object' &&
    'success' in payload &&
    (payload as SuccessEnvelope<T>).success === true &&
    'data' in payload
  ) {
    return (payload as SuccessEnvelope<T>).data;
  }
  return payload as T;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const SORTABLE_FIELDS = ['appliedAt', 'displayName'];

export function PendingVendorsPage(): JSX.Element {
  const { toast } = useToast();
  const [rows, setRows] = useState<PendingVendorRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [tableState, setTableState] = useState<TableState | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);
  const { t } = useTranslation('vendors');

  const fetchPending = useCallback(
    async (state: TableState): Promise<void> => {
      setLoading(true);
      try {
        const client = apiClient();
        const params = new URLSearchParams();
        params.set('page', String(state.page));
        params.set('limit', String(state.limit));
        if (state.sortBy) params.set('sortBy', state.sortBy);
        if (state.sortOrder) params.set('sortOrder', state.sortOrder);
        const path = `/api/v1/admin/vendors/pending?${params.toString()}`;
        const { data, error, response } = await (
          client as unknown as {
            GET: (
              p: string,
            ) => Promise<{
              data?: unknown;
              error?: unknown;
              response: Response;
            }>;
          }
        ).GET(path);
        if (error || !response.ok) {
          toast({
            title: t('pendingVendors.toast.loadFailTitle'),
            description: `HTTP ${response.status}`,
            variant: 'error',
          });
          setRows([]);
          setTotal(0);
          return;
        }
        const payload = unwrap<PendingVendorsResponse>(data);
        setRows(payload.vendors ?? []);
        setTotal(payload.pagination?.total ?? 0);
      } catch (err) {
        toast({
          title: t('pendingVendors.toast.networkError'),
          description: err instanceof Error ? err.message : t('pendingVendors.toast.unknownError'),
          variant: 'error',
        });
        setRows([]);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    },
    [toast, t],
  );

  // Re-fetch on table state change OR refresh tick (after approve/reject).
  useEffect(() => {
    if (!tableState) return;
    void fetchPending(tableState);
  }, [tableState, fetchPending, refreshTick]);

  const columns = useMemo<TableColumn<PendingVendorRow>[]>(
    () => [
      {
        id: 'displayName',
        header: t('pendingVendors.columns.vendor'),
        sortable: true,
        accessor: (row) => (
          <button
            type="button"
            className="text-pw-primary-strong hover:underline"
            onClick={() => setSelectedId(row.id)}
          >
            {row.displayName}
          </button>
        ),
      },
      {
        id: 'email',
        header: t('pendingVendors.columns.email'),
        accessor: (row) => row.email,
      },
      {
        id: 'countryCode',
        header: t('pendingVendors.columns.country'),
        accessor: (row) => row.countryCode,
        align: 'center',
      },
      {
        id: 'appliedAt',
        header: t('pendingVendors.columns.applied'),
        sortable: true,
        accessor: (row) => new Date(row.appliedAt).toLocaleString(),
      },
      {
        id: 'surveyCompletedAt',
        header: t('pendingVendors.columns.survey'),
        accessor: (row) =>
          row.surveyCompletedAt
            ? new Date(row.surveyCompletedAt).toLocaleDateString()
            : t('pendingVendors.columns.surveyPending'),
        align: 'center',
      },
    ],
    [t],
  );

  return (
    <main className="flex flex-col gap-4 p-6">
      <PageHeader
        title={t('pendingVendors.page.heading')}
        subtitle={t('pendingVendors.page.subtitle')}
      />

      <Table<PendingVendorRow>
        tableId="admin-pending-vendors"
        columns={columns}
        data={rows}
        total={total}
        loading={loading}
        sortableFields={SORTABLE_FIELDS}
        paginationMode="offset"
        pageSize={20}
        defaultSortBy="appliedAt"
        defaultSortOrder="desc"
        searchPlaceholder={t('pendingVendors.search.placeholder')}
        hideSearch
        emptyState={t('pendingVendors.empty')}
        onStateChange={setTableState}
      />

      {selectedId && (
        <VendorReviewDrawer
          vendorId={selectedId}
          onClose={() => setSelectedId(null)}
          onResolved={() => {
            setSelectedId(null);
            setRefreshTick((tick) => tick + 1);
          }}
        />
      )}
    </main>
  );
}
